import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Brain, Warning, TrendDown, Repeat, Clock, Target, ChartLine, Lightning, Heart, ArrowRight, CheckCircle, XCircle, BookOpen } from "phosphor-react";
import { updateSeoMetadata, getArticleAlternates } from "../../lib/seo";

const PorQueTradersNaoEvoluem = () => {
  useEffect(() => {
    updateSeoMetadata({
      title: 'Por Que Traders Não Evoluem | Psicologia do Trading',
      description: 'Descubra por que a maioria dos traders não evolui e como melhorar seus resultados com disciplina, dados e análise.',
      path: '/pt/por-que-traders-nao-evoluem',
      lang: 'pt-BR',
      alternates: getArticleAlternates('/pt/por-que-traders-nao-evoluem'),
    });
    
    return () => {
      document.title = 'GENI — Turn trading data into better decisions';
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <header className="border-b border-gray-800/50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/geni-logo.png" alt="GENI" className="h-8" />
            <span className="text-xl font-bold">GENI</span>
          </Link>
          <Link
            to="/signup"
            className="bg-[#00FF85] text-black px-5 py-2 rounded-lg font-semibold hover:bg-[#00DD70] transition-colors"
          >
            Começar Grátis
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Breadcrumb */}
        <nav className="text-sm text-gray-500 mb-8">
          <Link to="/" className="hover:text-[#00FF85]">Home</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-400">Psicologia do Trading</span>
        </nav>

        {/* Category Tag */}
        <div className="flex items-center gap-2 text-purple-400 text-sm font-medium mb-4">
          <Brain size={16} weight="bold" />
          <span>Psicologia do Trading</span>
        </div>

        {/* H1 */}
        <h1 className="text-4xl sm:text-5xl font-bold mb-4 leading-tight">
          Por Que a Maioria dos Traders Não Evolui
        </h1>

        <p className="text-xl text-gray-400 mb-12">
          (E Como Mudar Isso)
        </p>

        {/* Introdução */}
        <div className="prose prose-invert max-w-none">
          <p className="text-lg text-gray-300 leading-relaxed mb-6">
            Você já parou para pensar por que tantos traders passam meses — às vezes anos — operando sem melhorar? Eles estudam, assistem cursos, passam horas na frente do gráfico, mas os resultados continuam os mesmos. O esforço existe, mas a evolução não vem.
          </p>

          <p className="text-lg text-gray-300 leading-relaxed mb-12">
            Esse é um dos fenômenos mais frustrantes do trading. E a resposta não está na falta de conhecimento técnico. Está em algo mais profundo: <strong className="text-white">comportamento, estrutura e psicologia.</strong>
          </p>

          {/* Seção 1 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Lightning size={24} className="text-amber-400" />
              </div>
              <h2 className="text-2xl font-bold">A Ilusão de Progresso</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Muitos traders confundem atividade com evolução. Acreditam que fazer mais trades é sinônimo de melhorar. Mas não é.
            </p>

            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-6 mb-6">
              <p className="text-gray-300 leading-relaxed mb-4">
                <strong className="text-amber-400">Fazer mais trades ≠ melhorar.</strong>
              </p>
              <p className="text-gray-400 text-sm">
                Repetição sem análise não gera evolução — gera cristalização de erros. Você pode operar 1000 vezes e continuar cometendo os mesmos erros porque nunca parou para identificá-los.
              </p>
            </div>

            <p className="text-gray-300 leading-relaxed">
              O trader que faz 10 trades por dia sem revisar nenhum está apenas alimentando a ilusão de que está trabalhando duro. Mas trabalho duro sem direção é desperdício de energia.
            </p>
          </section>

          {/* Seção 2 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <TrendDown size={24} className="text-red-400" />
              </div>
              <h2 className="text-2xl font-bold">Falta de Feedback Estruturado</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              A maioria dos traders não tem um sistema de feedback. Eles operam, ganham ou perdem, e seguem para o próximo trade. Não existe um momento de parar e perguntar: <em className="text-white">"O que funcionou? O que não funcionou? Por quê?"</em>
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-red-400 mb-2">Sem Dados</h3>
                <p className="text-gray-400 text-sm">Traders não analisam seus números. Não sabem seu win rate real, seu RR médio, ou quais setups funcionam melhor.</p>
              </div>

              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-red-400 mb-2">Erros Repetidos</h3>
                <p className="text-gray-400 text-sm">Sem análise, os mesmos erros se repetem indefinidamente. O trader entra no ciclo de frustração sem saber como sair.</p>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Imagine um jogador de basquete que nunca assiste às gravações dos seus jogos. Ele pode treinar todos os dias, mas nunca vai corrigir seus erros de forma precisa. Trading é igual.
            </p>
          </section>

          {/* Seção 3 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Heart size={24} className="text-purple-400" />
              </div>
              <h2 className="text-2xl font-bold">Decisões Emocionais</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              O mercado é um ambiente hostil para a mente humana. Nosso cérebro evoluiu para evitar dor e buscar recompensa imediata — exatamente o oposto do que o trading exige.
            </p>

            <div className="space-y-4 mb-6">
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <XCircle size={20} className="text-red-400" />
                  <h3 className="font-semibold text-white">Medo</h3>
                </div>
                <p className="text-gray-400 text-sm">Faz você sair cedo demais de trades vencedores, mover stops para o breakeven antes da hora, ou simplesmente não entrar em setups válidos.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <XCircle size={20} className="text-red-400" />
                  <h3 className="font-semibold text-white">Ganância</h3>
                </div>
                <p className="text-gray-400 text-sm">Faz você segurar trades além do alvo, aumentar posição em momentos errados, ou ignorar sinais claros de saída porque "pode subir mais".</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <XCircle size={20} className="text-red-400" />
                  <h3 className="font-semibold text-white">Revenge Trading</h3>
                </div>
                <p className="text-gray-400 text-sm">Após uma perda, o trader quer "recuperar" imediatamente. Entra em trades sem setup, aumenta tamanho da posição, e transforma uma perda pequena em uma catástrofe.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <XCircle size={20} className="text-red-400" />
                  <h3 className="font-semibold text-white">Impulsividade</h3>
                </div>
                <p className="text-gray-400 text-sm">Entrar em trades porque "parece bom" sem confirmação do setup. Agir antes de pensar. O mercado castiga impulsividade com precisão cirúrgica.</p>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Emoções não são o inimigo — são humanas. O problema é deixá-las controlar suas decisões de trading.
            </p>
          </section>

          {/* Seção 4 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <ChartLine size={24} className="text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold">Falta de Controle e Registro</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Pergunte a um trader médio: <em className="text-white">"Qual seu setup mais lucrativo nos últimos 30 dias?"</em> A maioria não sabe responder. Pergunta de seguida: <em className="text-white">"Em qual horário você performa melhor?"</em> Silêncio.
            </p>

            <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-6 mb-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-blue-400 mb-3">Não sabem o que funciona</h3>
                  <p className="text-gray-400 text-sm">Podem ter um setup extremamente lucrativo, mas como não registram, não sabem. Continuam dando atenção igual a todos os setups.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-blue-400 mb-3">Não sabem o que não funciona</h3>
                  <p className="text-gray-400 text-sm">Podem estar sangrando dinheiro em um setup específico sem perceber. Sem dados, o prejuízo continua invisível.</p>
                </div>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Trading sem registro é como dirigir à noite sem faróis. Você pode até chegar a algum lugar, mas as chances de acidente são muito maiores.
            </p>
          </section>

          {/* Seção 5 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Repeat size={24} className="text-amber-400" />
              </div>
              <h2 className="text-2xl font-bold">Falta de Consistência</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Um dos padrões mais comuns entre traders que não evoluem é a inconsistência. Eles seguem as regras em alguns dias e quebram em outros. Às vezes por empolgação, às vezes por frustração.
            </p>

            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-white mb-4">O ciclo da inconsistência:</h3>
              <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
                <span className="px-4 py-2 bg-green-500/10 text-green-400 rounded-lg">Segue regras</span>
                <ArrowRight size={16} className="text-gray-600" />
                <span className="px-4 py-2 bg-green-500/10 text-green-400 rounded-lg">Ganha</span>
                <ArrowRight size={16} className="text-gray-600" />
                <span className="px-4 py-2 bg-amber-500/10 text-amber-400 rounded-lg">Fica confiante</span>
                <ArrowRight size={16} className="text-gray-600" />
                <span className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg">Quebra regras</span>
                <ArrowRight size={16} className="text-gray-600" />
                <span className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg">Perde</span>
                <ArrowRight size={16} className="text-gray-600" />
                <span className="px-4 py-2 bg-purple-500/10 text-purple-400 rounded-lg">Volta às regras</span>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Consistência não é sobre ser perfeito. É sobre fazer a mesma coisa repetidamente, independente do resultado do último trade. Isso exige disciplina — e disciplina é treinável.
            </p>
          </section>

          {/* Seção 6 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <Clock size={24} className="text-red-400" />
              </div>
              <h2 className="text-2xl font-bold">O Problema do Curto Prazo</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              O trader médio quer resultados imediatos. Quer ganhar dinheiro hoje, essa semana, esse mês. Essa mentalidade sabota a evolução de longo prazo.
            </p>

            <div className="grid sm:grid-cols-2 gap-6 mb-6">
              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-6">
                <h3 className="font-semibold text-red-400 mb-3">Mentalidade de Curto Prazo</h3>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li>• Foco no resultado do trade atual</li>
                  <li>• Decisões baseadas em emoção</li>
                  <li>• Frustração com perdas normais</li>
                  <li>• Desiste após sequência negativa</li>
                </ul>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-6">
                <h3 className="font-semibold text-[#00FF85] mb-3">Mentalidade de Longo Prazo</h3>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li>• Foco no processo e evolução</li>
                  <li>• Decisões baseadas em dados</li>
                  <li>• Aceita perdas como parte do jogo</li>
                  <li>• Persiste com ajustes constantes</li>
                </ul>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Trading é um jogo de longo prazo. Os traders que ganham consistentemente pensam em termos de meses e anos, não dias e semanas.
            </p>
          </section>

          {/* Seção 7 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Brain size={24} className="text-purple-400" />
              </div>
              <h2 className="text-2xl font-bold">Psicologia do Trading</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              A psicologia é responsável por 80% do sucesso no trading. Você pode ter o melhor sistema do mundo, mas se não tiver controle emocional, vai sabotar os próprios resultados.
            </p>

            <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-purple-400 mb-4">Conceitos-chave da psicologia do trading:</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-purple-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">Pensamento em probabilidades:</strong> Cada trade é uma aposta estatística. Você não controla o resultado individual, apenas a execução.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-purple-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">Controle emocional:</strong> Saber que vai sentir medo e ganância, mas não deixar essas emoções dirigirem suas decisões.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-purple-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">Desapego do resultado:</strong> Focar no processo, não no dinheiro. Execução perfeita é o objetivo, não lucro imediato.</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <BookOpen size={32} className="text-[#00FF85] flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-white mb-2">Leitura Essencial</h3>
                  <p className="text-gray-400 text-sm mb-2">
                    <strong className="text-white">"Trading in the Zone"</strong> de Mark Douglas é a bíblia da psicologia do trading. Douglas explica como a mente sabota traders e como desenvolver a mentalidade correta para operar com consistência.
                  </p>
                  <p className="text-gray-500 text-xs">O livro mostra que sucesso no trading não vem de prever o mercado, mas de executar seu sistema sem interferência emocional.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Seção 8 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#00FF85]/10 flex items-center justify-center">
                <Target size={24} className="text-[#00FF85]" />
              </div>
              <h2 className="text-2xl font-bold">O Que Realmente Faz um Trader Evoluir</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              A boa notícia: evolução no trading é possível. Mas exige um sistema estruturado, não apenas "mais estudo" ou "mais prática".
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <h3 className="font-semibold text-[#00FF85] mb-2">Análise Consistente</h3>
                <p className="text-gray-400 text-sm">Revisar cada trade sistematicamente. Não quando dá vontade, mas sempre. O hábito de análise é inegociável.</p>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <h3 className="font-semibold text-[#00FF85] mb-2">Dados</h3>
                <p className="text-gray-400 text-sm">Números não mentem. Win rate, RR, profit factor, drawdown. Conhecer suas métricas é conhecer a si mesmo como trader.</p>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <h3 className="font-semibold text-[#00FF85] mb-2">Disciplina</h3>
                <p className="text-gray-400 text-sm">Seguir as regras mesmo quando não quer. Especialmente quando não quer. Disciplina separa amadores de profissionais.</p>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <h3 className="font-semibold text-[#00FF85] mb-2">Repetição com Feedback</h3>
                <p className="text-gray-400 text-sm">Prática deliberada: executar, analisar, ajustar, repetir. Esse ciclo, mantido por meses, transforma qualquer trader.</p>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Não existe fórmula mágica. Existe trabalho estruturado, repetido ao longo do tempo. Os traders que evoluem são os que constroem sistemas de melhoria contínua.
            </p>
          </section>

          {/* Seção 9 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Lightning size={24} className="text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold">De Operador Aleatório para Trader Consistente</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              A transformação acontece quando você muda de mentalidade. Sai do modo "operador aleatório" e entra no modo "trader profissional".
            </p>

            <div className="grid sm:grid-cols-2 gap-6 mb-6">
              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-6">
                <h3 className="font-semibold text-red-400 mb-4">Operador Aleatório</h3>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li className="flex items-center gap-2">
                    <XCircle size={16} className="text-red-400" />
                    <span>Decisões baseadas em emoção</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <XCircle size={16} className="text-red-400" />
                    <span>Opera no achismo</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <XCircle size={16} className="text-red-400" />
                    <span>Não registra trades</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <XCircle size={16} className="text-red-400" />
                    <span>Culpa o mercado</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <XCircle size={16} className="text-red-400" />
                    <span>Busca resultados rápidos</span>
                  </li>
                </ul>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-6">
                <h3 className="font-semibold text-[#00FF85] mb-4">Trader Consistente</h3>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-[#00FF85]" />
                    <span>Decisões baseadas em dados</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-[#00FF85]" />
                    <span>Opera com sistema definido</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-[#00FF85]" />
                    <span>Registra e analisa tudo</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-[#00FF85]" />
                    <span>Assume responsabilidade</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-[#00FF85]" />
                    <span>Pensa em longo prazo</span>
                  </li>
                </ul>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              A mudança não acontece da noite para o dia. É gradual. Mas cada passo na direção certa compõe ao longo do tempo.
            </p>
          </section>

          {/* GENI Mention */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-[#00FF85]/10 via-[#00FF85]/5 to-transparent border border-[#00FF85]/20 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-4">Crie Seu Sistema de Feedback</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                Ferramentas como o <strong className="text-[#00FF85]">GENI</strong> ajudam traders a criar um sistema de feedback estruturado, facilitando a identificação de erros e padrões. Em vez de depender da memória e intuição, você tem dados concretos para guiar sua evolução.
              </p>
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 text-[#00FF85] font-semibold hover:underline"
              >
                Experimentar GENI Grátis <ArrowRight size={18} />
              </Link>
            </div>
          </section>

          {/* CTA Final */}
          <section className="mb-16">
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Pare de Operar no Achismo</h2>
              <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
                Se você quer evoluir no trading, precisa sair do achismo e começar a tomar decisões baseadas em dados. A diferença entre traders que evoluem e traders que estagnam está na estrutura, não no talento.
              </p>
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 bg-[#00FF85] text-black px-8 py-3 rounded-lg font-semibold hover:bg-[#00DD70] transition-colors"
              >
                Começar Grátis <ArrowRight size={20} />
              </Link>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-8">Perguntas Frequentes</h2>

            <div className="space-y-4">
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Por que traders perdem dinheiro?</h3>
                <p className="text-gray-400 text-sm">A maioria perde por falta de disciplina, decisões emocionais e ausência de um sistema estruturado de análise. Não é falta de conhecimento técnico — é falta de processo e controle psicológico.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Como evoluir no trading?</h3>
                <p className="text-gray-400 text-sm">Evolução vem de análise consistente, registro de todos os trades, identificação de padrões (positivos e negativos), e ajustes contínuos baseados em dados reais — não em feeling.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Psicologia influencia no trading?</h3>
                <p className="text-gray-400 text-sm">Sim, profundamente. Estima-se que 80% do sucesso no trading depende de psicologia. Medo, ganância, impulsividade e revenge trading destroem mais contas do que setups ruins.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Quanto tempo leva para melhorar no trading?</h3>
                <p className="text-gray-400 text-sm">Depende da intensidade do trabalho estruturado. Com análise diária consistente e ajustes baseados em dados, a maioria dos traders começa a ver melhorias significativas em 3-6 meses. Sem estrutura, pode levar anos — ou nunca acontecer.</p>
              </div>
            </div>
          </section>

          {/* Related Articles */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Artigos Relacionados</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link
                to="/pt/como-analisar-trades"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Como Analisar Seus Trades</h3>
                <p className="text-gray-500 text-sm">Guia prático para analisar trades de forma estruturada e melhorar seus resultados.</p>
              </Link>

              <Link
                to="/pt/diario-de-trades-automatico"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Diário de Trades Automático</h3>
                <p className="text-gray-500 text-sm">Como automatizar o registro dos seus trades e economizar tempo.</p>
              </Link>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800/50 py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img src="/geni-logo.png" alt="GENI" className="h-6" />
              <span className="font-semibold">GENI</span>
            </Link>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <Link to="/about" className="hover:text-white transition-colors">Sobre</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Termos</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacidade</Link>
            </div>
            <p className="text-sm text-gray-600">
              © 2025 GENI. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PorQueTradersNaoEvoluem;
